import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { Operatory } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/operatory.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { theme } from '@frontend/theme';
import { FormRow, TextField, useForm, Text, useAlert, ButtonBar, Button } from '@frontend/design-system';
import { useSchedulingLocationInfo } from '../../../hooks';
import { getOperatoryAvatarInfoByName, OperatoryAvatarInfo } from '../helpers';
import { OperatoryAvatarSelector } from '../operatory-avatar-selector';

type Props = {
  operatoryDetails?: Operatory;
  locationId?: string;
  onBack?: () => void;
};

export const EditOperatoryForm = ({ operatoryDetails, locationId, onBack }: Props) => {
  const { t } = useTranslation('scheduleGlobalSettings');
  const tokens = useTranslationTokens();
  const alert = useAlert();
  const { isMultiLocation } = useSchedulingLocationInfo();

  const updateOperatoryMutation = SchedulerV3.Mutations.useUpdateOperatoryMutation();

  const initialAvatarInfo = useMemo(
    () =>
      getOperatoryAvatarInfoByName(
        operatoryDetails?.externalName || operatoryDetails?.displayName || '',
        operatoryDetails?.details?.icon
      ),
    [operatoryDetails]
  );

  const [avatarInfo, setAvatarInfo] = useState<OperatoryAvatarInfo>(initialAvatarInfo);
  const handleIconSelection = (config: OperatoryAvatarInfo) => setAvatarInfo(config);

  const { formProps, getFieldProps } = useForm({
    fields: {
      operatoryName: {
        type: 'text',
        required: true,
        value: operatoryDetails?.externalName,
      },
      displayName: {
        type: 'text',
        value: operatoryDetails?.displayName ?? operatoryDetails?.externalName ?? '',
      },
    },
    onSubmit: (values) => {
      handleUpdateOperatory(values.displayName || '');
    },
  });

  const handleUpdateOperatory = (displayName: string) => {
    updateOperatoryMutation
      .mutateAsync({
        operatoryId: operatoryDetails?.id ?? '',
        hidden: operatoryDetails?.hidden,
        displayName,
        details: {
          ...operatoryDetails?.details,
          icon: {
            color: avatarInfo.color,
            name: avatarInfo.icon,
          },
        },
        childLocationId: isMultiLocation ? locationId || operatoryDetails?.locationId : undefined,
      })
      .then(() => {
        alert.success(t('{{operatory}} details updated successfully', { operatory: tokens.operatory }));
        onBack?.();
      })
      .catch(() => {
        alert.error(t('Failed to update {{operatory}} details', { operatory: tokens.operatory }));
      });
  };

  return (
    <section css={containerStyle}>
      <form {...formProps}>
        <FormRow css={{ marginBottom: theme.spacing(0) }}>
          <TextField
            disabled={true}
            {...getFieldProps('operatoryName')}
            label={t('{{operatory}} Name', { operatory: tokens.operatory })}
          />
        </FormRow>
        <Text css={{ marginTop: theme.spacing(1) }} size='small' color='light'>
          {t('The {{operatory}} name as it appears in your Practice Management System', {
            operatory: tokens.operatory,
          })}
        </Text>
        <FormRow cols={[15, 85]} css={{ margin: theme.spacing(2, 0, 0, 0) }}>
          <OperatoryAvatarSelector value={avatarInfo} onChange={handleIconSelection} />
          <div css={{ marginTop: theme.spacing(0.5) }}>
            <TextField {...getFieldProps('displayName')} label={t('Display Name')} />
            <Text css={{ marginTop: theme.spacing(1) }} size='small' color='light'>
              {t('This name will be visible on your Weave calendar.')}
            </Text>
          </div>
        </FormRow>

        <ButtonBar>
          <Button variant='secondary' onClick={onBack}>
            {t('Cancel')}
          </Button>
          <Button type='submit' loading={updateOperatoryMutation.isLoading}>
            {t('Save')}
          </Button>
        </ButtonBar>
      </form>
    </section>
  );
};

const containerStyle = css({
  width: '80%',
  padding: theme.spacing(4),
  backgroundColor: theme.colors.white,
  borderRadius: theme.borderRadius.medium,
});
